/**
 * The mark on a finished resource.
 *
 * One renderer for every document we produce – worksheets, quizzes, booklets,
 * workbooks and slide decks – so the same thing appears in the same place with
 * the same wording whichever of those a teacher happens to print. Three
 * separate versions of it is how a school ends up with a logo that looks
 * deliberate on a worksheet and like a mistake on a booklet cover.
 *
 * UMD, no DOM use – pure string building, exactly like worksheet-render.js.
 * Loaded in the browser as window.BrandMark and require()'d in Node by
 * utils/pdf.js and the two renderers.
 *
 * A mark is { name, logo, credit }, decided by utils/resourceMark.js and never
 * by this file – whose name goes on a document is a billing question, and this
 * is a renderer. What arrives here is one of three things:
 *
 *   a paying organisation's:  { name, logo, credit: 'Made by X' }
 *   the free tier's:          { credit: 'Made using BA Productivity' }
 *   a paying individual's:    null – nothing goes on the page
 *
 * `logo` is a data URI rather than a URL (see utils/orgBranding.js for why) and
 * is often absent: an organisation that has not uploaded one still gets its
 * name, which is the part that says whose resource this is.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.BrandMark = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    function esc(str) {
        return String(str == null ? '' : str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    /**
     * Is there a name or a crest to draw at the top of the document?
     *
     * False for the free tier's mark, which has a credit line but no name of
     * its own: our own logo does not belong at the top of somebody's worksheet,
     * and "BA Productivity" is not the answer to "whose sheet is this". The
     * free tier's only mark is the line at the foot.
     */
    function has(mark) {
        return !!(mark && (mark.name || mark.logo));
    }

    /**
     * The logo on its own, as an <img>.
     *
     * Empty string when there is no logo, so callers can concatenate without
     * guarding. The data URI is escaped on the way into the attribute even
     * though it was validated on upload: it lands in HTML, and the rule is that
     * everything landing in HTML is escaped where it lands.
     *
     * alt is the organisation's name rather than "logo": read aloud, "St
     * Aidan's High School" is the useful sentence and "logo" is not.
     */
    function logoImg(mark, className) {
        if (!mark || !mark.logo) return '';
        return `<img class="${esc(className)}" src="${esc(mark.logo)}" alt="${esc(mark.name || '')}">`;
    }

    /**
     * The strip that goes at the top of a document: crest, then the name.
     *
     * The name is repeated beside the logo rather than left to the credit line
     * at the foot, because a sheet handed out on its own is read from the top
     * and a crest alone is not always recognisable in greyscale.
     */
    function headerHtml(mark, prefix) {
        if (!has(mark)) return '';
        const p = prefix || 'brand';
        return `<div class="${esc(p)}-brand${mark.logo ? '' : ` ${esc(p)}-brand--text`}">`
            + logoImg(mark, `${p}-brand-logo`)
            + (mark.name ? `<span class="${esc(p)}-brand-name">${esc(mark.name)}</span>` : '')
            + `</div>`;
    }

    /** The line at the foot. Decided upstream – see the note at the top. */
    function creditText(mark) {
        return (mark && mark.credit) || '';
    }

    function creditHtml(mark, prefix) {
        if (!creditText(mark)) return '';
        const p = prefix || 'brand';
        // A logo-less credit is a line of pure text and needs its left padding
        // back – the tight left inset exists to sit snug against an image. A
        // modifier class rather than :not(:has(img)), because this string is
        // also printed by whatever browser a teacher happens to have.
        return `<p class="${esc(p)}-credit${mark.logo ? '' : ` ${esc(p)}-credit--text`}">`
            + logoImg(mark, `${p}-credit-logo`)
            + `<span>${esc(creditText(mark))}</span>`
            + `</p>`;
    }

    return { esc, has, logoImg, headerHtml, creditText, creditHtml };
}));
